import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { TrinitiDatePicker, TrinitiTimePicker } from '@/components/triniti-datetime';
import SearchSelect from '@/components/common/SearchSelect';
import LineItemSelect from '@/components/common/LineItemSelect';
import { participantLabel, PARTICIPANT_SEARCH_HINT, STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';
import { useAuth } from '@/lib/auth';
import { assignmentBlock } from '@/lib/friendlyCodes';
import { Pencil, User, Home, Users, Repeat, Plus, Trash2 } from 'lucide-react';
import { lineItemOption } from '@/components/common/LineItemSelect';
import { addItem, validateItems } from './extraServiceItems';   // client#2: same multi line item rules as Create Shift
import { authorisedOptions, selectorState, eligibilityState } from './createShiftServiceRules';
import { EligibilityNote, LOCKED_ROW_CLASS } from './ScreeningBadge';   // BS3-005
import { SHIFT_PERIODS, autoClassifyPeriod } from './ShiftPeriodBadge';
import { useStaffAvailabilityForShift, AvailabilityStatusBadge, useWorkerEligibility } from './StaffAvailabilityHelpers';
import { accidentalConflicts } from './shiftSheetRules';
import { SUPPORT_RATIOS, initialEditForm, validateEditForm, buildEditPayload, editErrorMessage, staffChange, initialItems, itemsPayload } from './shiftEditRules';

// Same three cards as Create Shift; only 1:1 shifts are edited here (SIL / centre have their own screens).
const SHIFT_TYPES = [
  { value: 'individual', label: '1:1 Support', icon: User },
  { value: 'sil', label: 'SIL House', icon: Home, why: 'SIL house shifts are edited on the house view' },
  { value: 'centre', label: 'Centre Session', icon: Users, why: 'Centre sessions are edited in the Centre Sessions builder' },
];

// Stable empty defaults: a fresh [] on every render would re-run the effects below without end
const NONE = [];

/** BS3-001: edit one shift with the same layout and rules as Create Shift (this occurrence only). */
export function EditShiftDialog({ shift, open, onClose, onSaved, participants = NONE, services = NONE, staff = NONE, currentPsiId = '', segments = NONE }) {
  const { user } = useAuth();
  const canWithoutAgreement = ['Admin', 'Super Admin'].includes(user?.role_name);
  const base = useMemo(() => initialEditForm(shift, currentPsiId), [shift, currentPsiId]);
  const startItems = useMemo(() => initialItems(segments, base.catalogue), [segments, base.catalogue]);
  // several items: the main service is the first one (allocation order)
  const initial = useMemo(() => (startItems.length ? { ...base, [base.catalogue ? 'service_id' : 'participant_service_item_id']: startItems[0].psi } : base), [base, startItems]);
  const [form, setForm] = useState(initial);
  const [items, setItems] = useState(startItems);   // client#2: [] = one service item covering the whole shift
  useEffect(() => { if (open) setItems(startItems); }, [open, startItems]);
  const [auth, setAuth] = useState(undefined);   // authorised services for participant + date: undefined = loading
  const [naCtx, setNaCtx] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setForm(initial); setError(''); } }, [open, initial]);
  useEffect(() => {
    if (!open || !form.participant_id || !form.date) { setAuth(null); return undefined; }
    let stale = false;
    setAuth(undefined);
    api.get(`/shifts/segments/authorised-services?participant_id=${form.participant_id}&service_date=${form.date}`)
      .then((r) => { if (!stale) setAuth(r.data?.data || r.data || null); })
      .catch(() => { if (!stale) setAuth(null); });
    return () => { stale = true; };
  }, [open, form.participant_id, form.date]);

  const psiOptions = authorisedOptions(auth);
  const sel = selectorState(auth, auth === undefined);
  // No agreement line on the date → Admin / Super Admin choose a catalogue line item (billed from the funding plan), as in Create Shift
  useEffect(() => {
    if (auth === undefined || !canWithoutAgreement || form.catalogue) return;
    if (psiOptions.length === 0) setForm((f) => ({ ...f, catalogue: true, participant_service_item_id: '' }));
  }, [auth]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !form.catalogue || !form.participant_id || !form.date) { setNaCtx(null); return undefined; }
    let stale = false;
    const q = new URLSearchParams({ participant_id: form.participant_id, shift_date: form.date, ...(form.service_id ? { service_id: form.service_id } : {}),
      start_time: form.start_time || '', end_time: form.end_time || '' });
    api.get(`/roster/no-agreement-context?${q}`).then((r) => { if (!stale) setNaCtx(r.data?.data || null); }).catch(() => { if (!stale) setNaCtx(null); });
    return () => { stale = true; };
  }, [open, form.catalogue, form.participant_id, form.date, form.service_id, form.start_time, form.end_time]);

  // Opened from a screen that has not loaded the catalogue (e.g. the participant's Rosters tab): load it here
  const [ownServices, setOwnServices] = useState([]);
  useEffect(() => {
    if (!open || !form.catalogue || (services || []).length || ownServices.length) return;
    api.get('/services/catalog', { params: { mode: 'selection' } }).then((r) => setOwnServices(r.data?.items || r.data?.data || [])).catch(() => {});
  }, [open, form.catalogue]); // eslint-disable-line react-hooks/exhaustive-deps
  const catalogue = (services || []).length ? services : ownServices;

  const participantOptions = useMemo(() => {
    const opts = (participants || []).map((p) => ({ value: p.id, label: participantLabel(p), keywords: p.ndis_number || '' }));
    const pid = shift?.participant_id;
    if (pid && !opts.some((o) => o.value === pid)) opts.unshift({ value: pid, label: shift.participant_name || 'Current participant' });
    return opts;
  }, [participants, shift]);

  // Staff picker with the same availability / conflict / eligibility hints as Create Shift (this shift is excluded from conflicts)
  const { statusMap, sortedStaff, enabled: availEnabled } = useStaffAvailabilityForShift(staff, { date: form.date, startTime: form.start_time, endTime: form.end_time, excludeShiftId: shift?.id });
  const eligibility = useWorkerEligibility(staff, null, { date: form.date, participant_id: form.participant_id, linked_participants: [], start_time: form.start_time, end_time: form.end_time });
  const staffOptions = sortedStaff.map((s) => {
    const key = s.user_id || s.id;
    const status = availEnabled ? statusMap[key]?.status : null;
    const conflict = accidentalConflicts(statusMap, [{ id: key, name: s.full_name }], [])[0];
    const elig = eligibilityState(key, eligibility);
    const keep = key === initial.staff_id;   // the current worker always stays selectable
    return {
      value: key, label: s.full_name || '', keywords: `${s.email || ''} ${s.employee_id || ''}`,
      disabled: !keep && (!!conflict || elig.disabled), title: conflict?.reason || elig.reason || undefined,
      className: elig.screening ? LOCKED_ROW_CLASS : undefined,   // BS3-005
      render: (
        <span className="inline-flex items-center gap-2 w-full">
          <span className="truncate">{s.full_name}</span>
          {status && !elig.disabled ? <AvailabilityStatusBadge status={status} /> : null}
          <EligibilityNote elig={elig} />
          {conflict ? <span className="text-[10px] text-red-700 truncate">{conflict.reason}</span> : null}
        </span>
      ),
    };
  });
  const manyWorkers = (shift?.assigned_staff_ids || []).length > 1;

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setError('');
    if ('participant_id' in patch || 'catalogue' in patch) setItems([]);   // another participant / billing mode → start again with one item
  };

  // client#2: several service items (agreement lines, or catalogue line items) — same editor as Create Shift
  const mainItemValue = form.catalogue ? form.service_id : form.participant_service_item_id;
  const rows = items.map((it, i) => (i === 0 ? { ...it, psi: mainItemValue } : it));
  const itemOptions = form.catalogue ? (catalogue || []).map((s) => lineItemOption(s, { showRate: false })) : psiOptions;
  const optionLabel = (v) => (itemOptions.find((o) => o.value === v) || {}).label || '';
  const updateItem = (key, patch) => { setItems((prev) => prev.map((it) => (it._key === key ? { ...it, ...patch } : it))); setError(''); };
  const removeItem = (key) => setItems((prev) => { const next = prev.filter((it) => it._key !== key); return next.length > 1 ? next : []; });
  const handleAddItem = () => setItems(addItem(form, rows.length ? rows : [{ _key: 'main', psi: mainItemValue, start: form.start_time, end: form.end_time }]));
  const itemsEditor = mainItemValue ? (
    <div className="space-y-1.5 pt-1" data-testid="edit-shift-items">
      {rows.length > 1 && (
        <>
          <p className="text-[11px] text-muted-foreground">Each service item covers its own part of the shift ({form.start_time}–{form.end_time}).</p>
          {rows.map((it, i) => (
            <div key={it._key} className="grid grid-cols-[1fr_auto_auto_auto] gap-1.5 items-center min-w-0" data-testid={`edit-shift-item-row-${i}`}>
              {i === 0
                ? <span className="text-xs truncate" title={optionLabel(it.psi)}>Main: {optionLabel(it.psi) || '—'}</span>
                : <LineItemSelect options={itemOptions} value={it.psi} onChange={(v) => updateItem(it._key, { psi: v })}
                    placeholder={form.catalogue ? 'Select the line item' : 'Select the authorised service'} className="h-8 text-xs" testId={`edit-shift-item-${i}`} />}
              <TrinitiTimePicker value={it.start} onChange={(v) => updateItem(it._key, { start: v })} testId={`edit-shift-item-start-${i}`} />
              <TrinitiTimePicker value={it.end} onChange={(v) => updateItem(it._key, { end: v })} testId={`edit-shift-item-end-${i}`} />
              {i === 0 ? <span className="w-7" /> : (
                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeItem(it._key)} aria-label="Remove service item" data-testid={`edit-shift-item-remove-${i}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </>
      )}
      <button type="button" className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" onClick={handleAddItem} data-testid="edit-shift-add-item">
        <Plus className="h-3 w-3" /> Add service item
      </button>
    </div>
  ) : null;
  const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const autoPreview = form.shift_period === 'AUTO' ? autoClassifyPeriod(form.start_time, form.end_time) : null;

  const save = async () => {
    const problem = validateEditForm(form) || (rows.length > 1 ? validateItems(form, rows) : null);
    if (problem) { setError(problem); return; }
    let payload = buildEditPayload(shift, form, initial);
    const itemList = itemsPayload(form, rows, startItems, initial);
    if (itemList) payload = { ...(payload || {}), items: itemList };
    const staffStep = manyWorkers ? null : staffChange(initial.staff_id, form.staff_id);
    if (!payload && !staffStep) { onClose(); return; }
    setSaving(true);
    try {
      if (payload) {
        const res = await api.put(`/roster/shifts/${shift.id}/edit`, payload);
        (res.data?.warnings || []).forEach((w) => toast.warning('Billed from the funding plan', { description: String(w), duration: 8000 }));
      }
    } catch (e) {
      setError(editErrorMessage(e));   // stays open so nothing typed is lost
      setSaving(false);
      return;
    }
    if (staffStep) {   // same endpoints as the Coverage tab, checked for the saved time / participant
      try {
        if (staffStep.remove) await api.post(`/roster/shifts/${shift.id}/remove-staff`, { staff_id: staffStep.remove });
        if (staffStep.add) await api.post(`/roster/shifts/${shift.id}/add-staff`, { staff_id: staffStep.add });
      } catch (e) {
        const blk = assignmentBlock(e);
        toast.warning(payload ? 'Shift saved, but the worker could not be changed' : 'The worker could not be changed',
          { description: blk ? blk.reasons.join(' · ') || blk.title : editErrorMessage(e), duration: 10000 });
        onSaved?.(); setSaving(false); onClose(); return;
      }
    }
    toast.success('Shift updated');
    onSaved?.();
    setSaving(false);
    onClose();
  };

  if (!shift) return null;
  const recurring = shift.recurring || shift.parent_recurring_id;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      {/* BR7 / TRI-CR-007: an outside click never closes a form with unsaved changes */}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-shift-dialog-board"
        onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => { if (saving) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4" /> Edit Shift</DialogTitle>
          <DialogDescription>Changes apply to this shift only{recurring ? ' — the recurring series is not changed' : ''}.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2" data-testid="edit-shift-type">
          {SHIFT_TYPES.map((t) => {
            const Icon = t.icon;
            const active = t.value === 'individual';
            return (
              <div key={t.value} title={t.why} aria-disabled={!active}
                className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-2 text-xs ${active ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-slate-200 text-slate-400'}`}>
                <Icon className="w-4 h-4" /><span>{t.label}</span>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground -mt-2">
          One participant, one or more services.{recurring && <span className="inline-flex items-center gap-1 ml-1" data-testid="edit-shift-recurring-note"><Repeat className="w-3 h-3" /> Recurring — only this occurrence is edited.</span>}
        </p>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1 min-w-0"><Label className="text-xs">Date</Label><TrinitiDatePicker value={form.date} onChange={(v) => set({ date: v })} testId="edit-shift-date" /></div>
            <div className="space-y-1 min-w-0"><Label className="text-xs">Start</Label><TrinitiTimePicker value={form.start_time} onChange={(v) => set({ start_time: v })} testId="edit-shift-start" /></div>
            <div className="space-y-1 min-w-0"><Label className="text-xs">End</Label><TrinitiTimePicker value={form.end_time} onChange={(v) => set({ end_time: v })} testId="edit-shift-end" /></div>
          </div>

          <div className="space-y-1 min-w-0">
            <Label className="text-xs">Participant</Label>
            <SearchSelect options={participantOptions} value={form.participant_id}
              onChange={(v) => set({ participant_id: v, participant_service_item_id: '', agreement_line_id: '', service_id: '', catalogue: false })}
              placeholder="Select participant" searchPlaceholder={PARTICIPANT_SEARCH_HINT} emptyText="No participants match that search"
              className="h-8 text-xs" testId="edit-shift-participant" />
          </div>

          {form.catalogue ? (
            <div className="space-y-1 min-w-0" data-testid="edit-shift-catalogue">
              <Label className="text-xs">Line item <span className="text-muted-foreground">(no service agreement — billed from the funding plan)</span></Label>
              <LineItemSelect items={catalogue} showRate={false} value={form.service_id} placeholder="Select the line item"
                onChange={(v) => set({ service_id: v })} className="h-8 text-xs" testId="edit-shift-line-item" />
              {itemsEditor}
              <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 space-y-1" data-testid="edit-shift-no-agreement-panel">
                {psiOptions.length > 0 && (
                  <button type="button" className="text-primary hover:underline" data-testid="edit-shift-use-agreement"
                    onClick={() => set({ catalogue: false, service_id: '' })}>← Use the agreement line instead</button>
                )}
                <p className="font-medium">{psiOptions.length > 0 ? 'Line item chosen from the service catalogue instead of the agreement line.' : `This participant has no active service agreement line for ${form.date}.`}</p>
                {naCtx?.funding_plan && (
                  <p>Funding plan <b>{naCtx.funding_plan.name || 'Plan'}</b> ({naCtx.funding_plan.start_date || '—'} – {naCtx.funding_plan.end_date || '—'}) · remaining {money(naCtx.funding_plan.remaining_budget)}
                    {naCtx.funding_plan.category_remaining != null && <> · {naCtx.funding_plan.category} remaining {money(naCtx.funding_plan.category_remaining)}</>}</p>
                )}
                {(naCtx?.warnings || []).map((w) => <p key={w.code} className="text-amber-800">⚠ {w.message}</p>)}
              </div>
            </div>
          ) : (
            <div className="space-y-1 min-w-0" data-testid="edit-shift-agreement">
              <Label className="text-xs">Authorised service <span className="text-muted-foreground">(ACTIVE agreement line → PSI, §14)</span></Label>
              <LineItemSelect options={psiOptions} value={form.participant_service_item_id} disabled={sel.disabled}
                placeholder={sel.disabled ? sel.reason : 'Select the authorised service'}
                onChange={(v) => { const o = psiOptions.find((x) => x.value === v); set({ participant_service_item_id: v, agreement_line_id: o?.agreement_line_id || '' }); }}
                className="h-8 text-xs" testId="edit-shift-service" />
              {sel.disabled && auth !== undefined && <p className="text-[10px] text-amber-700" data-testid="edit-shift-service-reason">{sel.reason}</p>}
              {itemsEditor}
              {canWithoutAgreement && auth !== undefined && psiOptions.length > 0 && (
                <button type="button" className="text-[11px] text-primary hover:underline" data-testid="edit-shift-use-catalogue"
                  onClick={() => set({ catalogue: true, participant_service_item_id: '', agreement_line_id: '' })}>
                  Use a catalogue line item instead (billed from the funding plan)
                </button>
              )}
            </div>
          )}

          <div className="space-y-1 min-w-0">
            <Label className="text-xs">Ratio</Label>
            <Select value={form.support_ratio} onValueChange={(v) => set({ support_ratio: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="edit-shift-ratio"><SelectValue /></SelectTrigger>
              <SelectContent>{SUPPORT_RATIOS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            {form.support_ratio === '2:1' && <p className="text-[10px] text-muted-foreground">A 2:1 shift needs two workers — add the second one in the Coverage tab.</p>}
          </div>

          <div className="space-y-1 min-w-0">
            <Label className="text-xs">Assign Staff <span className="text-muted-foreground">(optional)</span></Label>
            {manyWorkers ? (
              <p className="text-xs rounded-md border px-2.5 py-1.5 text-slate-700" data-testid="edit-shift-staff-many">
                {(shift.assigned_staff || []).map((s) => s.name).join(', ') || `${shift.assigned_staff_ids.length} workers`} — change several workers in the Coverage tab.
              </p>
            ) : (
              <SearchSelect options={staffOptions} value={form.staff_id || 'none'} onChange={(v) => set({ staff_id: v === 'none' ? '' : v })}
                placeholder="No staff (open shift)" searchPlaceholder={STAFF_SEARCH_HINT} emptyText="No staff match that search"
                clearLabel="None (Open Shift)" clearValue="none" className="h-8 text-xs" testId="edit-shift-staff" />
            )}
          </div>

          <div className="space-y-1 min-w-0">
            <Label className="text-xs">Shift Period</Label>
            <Select value={form.shift_period} onValueChange={(v) => set({ shift_period: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="edit-shift-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AUTO">Auto-detect{autoPreview ? ` (${autoPreview})` : ''}</SelectItem>
                {SHIFT_PERIODS.map((p) => <SelectItem key={p} value={p}>{p.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">Sleep-Over must be selected manually — it is never auto-classified.</p>
          </div>

          <div className="space-y-1 min-w-0">
            <Label className="text-xs">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} className="text-xs" data-testid="edit-shift-notes" />
          </div>

          {error && <p className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700" role="alert" data-testid="edit-shift-error">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="edit-shift-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-shift-save">{saving ? 'Saving…' : 'Save 1:1 Support'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditShiftDialog;
